<template>
  <default-layout>
    <div class="aa-track-order">
      <v-container>
        <h1 class="text-center mb-8">Track Your Order</h1>

        <v-row justify="center">
          <v-col cols="12" md="8" lg="6">
            <v-card flat class="pa-6 elevation-2">
              <v-alert v-if="!order" type="info" text class="mb-6">
                <strong>Guest Order Tracking</strong><br>
                Enter your email and order token to view your order status.
                Check your confirmation email for the token.
              </v-alert>

              <v-form v-if="!order" @submit.prevent="trackOrder" ref="form">
                <label class="aa-form-label">
                  <h4>Email Address</h4>
                  <v-text-field
                    v-model="email"
                    type="email"
                    placeholder="Enter your email"
                    :rules="emailRules"
                    required
                    solo
                    flat
                    hide-details="auto"
                    class="mb-4"
                  ></v-text-field>
                </label>

                <label class="aa-form-label">
                  <h4>Order Token</h4>
                  <v-text-field
                    v-model="orderToken"
                    placeholder="Enter your order token"
                    :rules="requiredRules"
                    required
                    solo
                    flat
                    hide-details="auto"
                    hint="Check your confirmation email for the order token"
                    persistent-hint
                    class="mb-6"
                  ></v-text-field>
                </label>

                <v-btn
                  type="submit"
                  :loading="loading"
                  :disabled="loading"
                  block
                  large
                  color="primary"
                  class="btn btn-primary arrow mt-4"
                >
                  Track Order
                </v-btn>
              </v-form>

              <!-- Order Details -->
              <div v-if="order" class="order-details">
                <v-alert type="success" border="left" colored-border class="mb-6">
                  <strong>✓ Order Found!</strong>
                  <div class="mt-1">Your order has been successfully retrieved.</div>
                </v-alert>

                <div class="order-summary mb-6">
                  <h2 class="mb-4" style="color: var(--burgundy);">Order Summary</h2>
                  
                  <v-card flat outlined class="pa-4 mb-4">
                    <v-row>
                      <v-col cols="6" class="py-2">
                        <div class="text-caption grey--text">Order ID</div>
                        <div class="font-weight-bold">#{{ order.id }}</div>
                      </v-col>
                      <v-col cols="6" class="py-2">
                        <div class="text-caption grey--text">Status</div>
                        <v-chip :color="getStatusColor(order.status)" small label>
                          {{ order.status }}
                        </v-chip>
                      </v-col>
                      <v-col cols="6" class="py-2">
                        <div class="text-caption grey--text">Order Date</div>
                        <div>{{ formatDate(order.created_at) }}</div>
                      </v-col>
                      <v-col cols="6" class="py-2">
                        <div class="text-caption grey--text">Total</div>
                        <div class="font-weight-bold text-h6" style="color: var(--burgundy);">${{ order.total }}</div>
                      </v-col>
                    </v-row>
                  </v-card>
                </div>

                <v-divider class="my-6"></v-divider>

                <div class="shipping-info">
                  <h3 class="mb-3" style="color: var(--burgundy);">Shipping Address</h3>
                  <v-card flat outlined class="pa-4">
                    <p class="mb-1 font-weight-medium">{{ order.shipping_address_name }}</p>
                    <p class="mb-1">{{ order.shipping_address_line_1 }}</p>
                    <p v-if="order.shipping_address_line_2" class="mb-1">{{ order.shipping_address_line_2 }}</p>
                    <p>{{ order.shipping_address_city }}, {{ order.shipping_address_state }} {{ order.shipping_address_postal_code }}</p>
                  </v-card>
                </div>

                <v-btn
                  @click="resetTracking"
                  outlined
                  color="primary"
                  class="mt-6 btn btn-primary"
                  large
                >
                  Track Another Order
                </v-btn>
              </div>

              <v-alert v-if="error" type="error" class="mt-4">
                {{ error }}
              </v-alert>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </default-layout>
</template>

<script>
import axios from 'axios';

export default {
  name: 'TrackOrder',
  data() {
    return {
      email: this.$route.query.email || '',
      orderToken: this.$route.query.token || '',
      order: null,
      error: null,
      loading: false,
      emailRules: [
        v => !!v || 'Email is required',
        v => /.+@.+\..+/.test(v) || 'Email must be valid'
      ],
      requiredRules: [
        v => !!v || 'This field is required'
      ]
    };
  },
  methods: {
    async trackOrder() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.loading = true;
      this.error = null;

      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/api/orders/track-guest`,
          {
            email: this.email,
            order_token: this.orderToken
          }
        );

        this.order = response.data.order;
      } catch (error) {
        this.error = 'Order not found. Please check your email and order token.';
      } finally {
        this.loading = false;
      }
    },
    resetTracking() {
      this.order = null;
      this.error = null;
      this.email = '';
      this.orderToken = '';
      this.$refs.form.resetValidation();
    },
    getStatusColor(status) {
      const colors = {
        processing: 'orange',
        completed: 'green',
        cancelled: 'red',
        pending: 'blue'
      };
      return colors[status] || 'grey';
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  },
  mounted() {
    // Auto-track if email and token in URL
    if (this.email && this.orderToken) {
      this.trackOrder();
    }
  }
};
</script>

<style scoped lang="scss">
.aa-track-order {
  padding: 60px 0;
  min-height: 60vh;
  
  h1 {
    color: var(--burgundy);
    font-size: 2.5rem;
    font-weight: 600;
  }

  .aa-form-label {
    display: block;
    margin-bottom: 0;
    
    h4 {
      font-size: 14px;
      font-weight: 600;
      margin-bottom: 8px;
      color: #333;
    }
  }

  .order-details {
    h2, h3 {
      font-weight: 600;
    }
  }

  .btn-primary {
    background: var(--burgundy) !important;
    color: white !important;
    
    &:hover {
      background: var(--red) !important;
    }
  }
}
</style>

